<template>
  <el-dialog
    :visible.sync="dialogVisible"
    title="分享智能体"
    width="500px"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    @close="handleClose"
    class="share-agent-dialog"
    center
    append-to-body
  >
    <div class="dialog-content">
      <!-- 智能体名称 -->
      <div class="agent-name-section">
        <span class="agent-name">{{ agentName }}</span>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <i class="el-icon-loading loading-icon"></i>
        <p>正在生成分享链接...</p>
      </div>

      <!-- 错误提示 -->
      <div v-if="error" class="error-container">
        <p class="error-text">{{ error }}</p>
        <el-button type="primary" size="small" @click="loadShareLink">
          重试
        </el-button>
      </div>

      <!-- 分享链接展示 -->
      <div v-if="shareData && !loading && !error" class="share-content">
        <!-- 分享链接 -->
        <div class="share-url-section">
          <label class="section-label">分享链接:</label>
          <div class="url-box" @click="copyToClipboard">
            <p class="url-text">{{ shareData.shareUrl }}</p>
          </div>
          <p class="url-hint">点击链接可复制</p>
        </div>

        <!-- 二维码 -->
        <div class="qrcode-section">
          <label class="section-label">扫描二维码访问:</label>
          <div class="qrcode-container">
            <canvas
              ref="qrcodeCanvas"
              class="qrcode-canvas"
            ></canvas>
            <!-- 中间的头像 -->
            <div class="qrcode-avatar">
              <head-image
                :size="56"
                :name="agentName"
                :url="agentAvatar"
                :style="{ borderRadius: '8px' }"
              />
            </div>
          </div>
        </div>

        <!-- 提示信息 -->
        <div class="info-container">
          <p class="info-text">分享链接永久有效，任何人都可以通过链接访问该智能体</p>
        </div>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button
        v-if="shareData && !loading"
        type="primary"
        @click="copyToClipboard"
      >
        {{ copied ? '已复制!' : '复制链接' }}
      </el-button>
      <el-button @click="handleClose">关闭</el-button>
    </span>
  </el-dialog>
</template>

<script>
import HeadImage from "../common/HeadImage.vue";
import QRCode from 'qrcode';

export default {
  name: "ShareAgentDialog",
  components: { HeadImage },
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    agentId: {
      type: String,
      required: true
    },
    agentName: {
      type: String,
      default: '智能体'
    },
    agentAvatar: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      dialogVisible: this.visible,
      loading: false,
      error: null,
      shareData: null,
      copied: false
    };
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal;
      if (newVal && !this.shareData && !this.loading) {
        this.loadShareLink();
      }
    },
    dialogVisible(newVal) {
      if (!newVal) {
        this.$emit('update:visible', false);
      }
    },
    shareData: {
      handler(newVal) {
        if (newVal && newVal.shareUrl) {
          // 当分享数据更新时，生成二维码
          this.$nextTick(() => {
            this.generateQRCode();
          });
        }
      },
      immediate: true
    }
  },
  mounted() {
    if (this.visible && !this.shareData) {
      this.loadShareLink();
    }
  },
  methods: {
    async generateQRCode() {
      if (!this.shareData || !this.shareData.shareUrl) {
        return;
      }

      const canvas = this.$refs.qrcodeCanvas;
      if (!canvas) {
        console.warn('[ShareAgentDialog] Canvas元素未找到');
        return;
      }

      try {
        // 使用qrcode库生成二维码到canvas
        await QRCode.toCanvas(canvas, this.shareData.shareUrl, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          errorCorrectionLevel: 'H' // 高容错率，即使中间有头像也能扫描
        });
      } catch (err) {
        console.error('[ShareAgentDialog] 生成二维码失败:', err);
      }
    },

    async loadShareLink() {
      try {
        this.loading = true;
        this.error = null;

        const response = await this.$http({
          url: `/agent/share`,
          method: 'post',
          data: { agentId: this.agentId }
        });

        // axios拦截器已经处理过响应，直接就是数据对象
        if (response && response.shareId && response.shareUrl) {
          this.shareData = {
            shareId: response.shareId,
            shareUrl: response.shareUrl,
            createdTime: response.createdTime
          };
        } else {
          throw new Error('生成分享链接失败：响应数据格式错误');
        }
      } catch (err) {
        console.error('[ShareAgentDialog] 生成分享链接失败:', err);
        this.error = err.response?.data?.msg || err.message || '生成分享链接失败，请稍后重试';
      } finally {
        this.loading = false;
      }
    },

    async copyToClipboard() {
      if (!this.shareData || !this.shareData.shareUrl) return;

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.shareData.shareUrl);
        } else {
          // 降级方案：使用 textarea 复制
          const textarea = document.createElement('textarea');
          textarea.value = this.shareData.shareUrl;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }

        this.copied = true;
        this.$message.success('链接已复制到剪贴板');

        // 2秒后重置
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        console.error('[ShareAgentDialog] 复制失败:', err);
        this.$message.error('复制失败，请手动复制');
      }
    },

    handleClose() {
      this.dialogVisible = false;
      this.$emit('close');
    }
  }
};
</script>

<style scoped lang="scss">
.share-agent-dialog {
  // 修复遮罩层遮挡问题
  ::v-deep .el-dialog__wrapper {
    z-index: 2001 !important;
  }

  ::v-deep .el-dialog {
    z-index: 2002 !important;
  }

  ::v-deep .el-dialog__header {
    text-align: center;
    padding-bottom: 12px;
  }

  ::v-deep .el-dialog__body {
    padding: 20px 24px;
  }

  .dialog-content {
    min-height: 200px;
  }

  // 智能体名称
  .agent-name-section {
    text-align: center;
    margin-bottom: 20px;

    .agent-name {
      font-size: 16px;
      color: #999;
      font-weight: normal;
    }
  }

  // 加载状态
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 0;

    .loading-icon {
      font-size: 32px;
      color: #409EFF;
      margin-bottom: 12px;
    }

    p {
      color: #999;
      margin: 0;
      font-size: 14px;
    }
  }

  // 错误状态
  .error-container {
    text-align: center;
    padding: 20px 0;

    .error-text {
      color: #f56c6c;
      margin: 0 0 16px 0;
      font-size: 14px;
    }
  }

  // 分享内容
  .share-content {
    .section-label {
      display: block;
      font-size: 14px;
      color: #333;
      margin-bottom: 8px;
    }

    // 分享链接部分
    .share-url-section {
      margin-bottom: 20px;

      .url-box {
        background-color: #f5f7fa;
        border-radius: 8px;
        padding: 12px;
        margin-bottom: 4px;
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
          background-color: #e8ecf0;
        }

        .url-text {
          color: #409EFF;
          font-size: 14px;
          margin: 0;
          text-align: center;
          word-break: break-all;
          line-height: 1.5;
        }
      }

      .url-hint {
        font-size: 12px;
        color: #999;
        margin: 0;
        text-align: center;
        font-style: italic;
      }
    }

    // 二维码部分
    .qrcode-section {
      margin-bottom: 20px;
      text-align: center;

      .qrcode-container {
        position: relative;
        display: inline-block;
        padding: 12px;
        background: #fff;
        border-radius: 8px;
        border: 1px solid #eaeaea;
        margin-top: 8px;

        .qrcode-canvas {
          display: block;
        }

        // 中间的头像
        .qrcode-avatar {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          border-radius: 8px !important;
          overflow: hidden;

          // 强制矩形头像，覆盖任何圆形样式
          ::v-deep .head-image,
          ::v-deep img,
          ::v-deep canvas {
            border-radius: 8px !important;
            display: block;
          }
        }
      }
    }

    // 提示信息
    .info-container {
      background-color: #f5f7fa;
      border-radius: 8px;
      padding: 12px;

      .info-text {
        font-size: 12px;
        color: #999;
        margin: 0;
        text-align: center;
        line-height: 1.5;
      }
    }
  }

  // 底部按钮
  .dialog-footer {
    display: flex;
    justify-content: center;
    gap: 12px;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .share-agent-dialog {
    ::v-deep .el-dialog {
      width: 90% !important;
      margin: 20px auto !important;
    }

    .qrcode-section {
      .qrcode-container {
        .qrcode-image {
          width: 150px !important;
          height: 150px !important;
        }
      }
    }
  }
}
</style>
